import { useQuery } from '@tanstack/react-query';
import {
  isConfigurableCapabilityRole,
  roleMay,
  type RoleCapability,
  type SystemRole,
} from '@peles/shared';
import { getMyCapabilities } from '../api/capabilities';
import { useAuth } from '../context/AuthContext';

export const CAPABILITIES_KEY = ['capabilities'] as const;

/**
 * Whether the signed-in person's role may do `capability` — the screen's half
 * of the check `authorizeCapability` makes on the server, so a control the
 * office has withdrawn is not offered only to be refused.
 *
 * Only a role the grid governs asks the server; for any other the answer is
 * `roleMay`'s, which is the server's own rule.
 *
 * ⚠️ **A planner is offered nothing until the server has answered, and asked
 * again on every mount and every return to the tab.** An administrator's
 * change reaches the planner's browser by no other route, so a cached "yes"
 * would keep offering a control the server now refuses; and the defaults
 * standing in until the first answer would offer, on every page load, what
 * the office has withdrawn. Keyed by user, since impersonation switches role
 * without a reload.
 */
export function useCan(capability: RoleCapability): boolean {
  const { user } = useAuth();
  const role = (user?.systemRole ?? null) as SystemRole | null;
  const governed = role !== null && isConfigurableCapabilityRole(role);
  const { data } = useQuery({
    queryKey: [...CAPABILITIES_KEY, 'mine', user?.id ?? null],
    queryFn: getMyCapabilities,
    enabled: governed,
    staleTime: 0,
    refetchOnWindowFocus: 'always',
  });
  if (role === null) return false;
  if (!governed) return roleMay(role, capability);
  if (!data) return false;
  return data.includes(capability);
}
